import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import TabClient from '../tabs/TabClient';
import { apiGet, apiPut } from '../utils/api-client';
import { useToast } from '../components/Toast';

/**
 * TabClientContainer
 *
 * Endpoints branchés :
 *  - GET /api/restaurants/config       → config existante (page client)
 *  - PUT /api/restaurants/config       → sauvegarde (couleurs, messages)
 *  - GET /api/dashboard/               → fallback pour récupérer le slug
 *
 * ✅ Sprint 1 P0 (mai 2026) — Persistance complète activée :
 *   - Migration DB : 6 colonnes ajoutées sur `restaurants`
 *     (`message_accueil`, `message_satisfait`, `message_decu`,
 *      `couleur_accent`, `couleur_fond`, `logo_url`)
 *   - Whitelist backend `CONFIG_FIELDS_SCHEMA` étendue avec validation stricte
 *     (regex hex pour couleurs, https-only pour logo, cap 500 chars messages)
 *   - GET /api/r/:slug expose les 6 champs au funnel public (QRPage.js)
 *
 * Hotfix d'origine du 04/05/2026 conservé pour traçabilité : alignement
 * des noms de props avec `tabs/TabClient.jsx` qui attend `restoName /
 * msgWelcome / msgThanks / msgFeedback / colorPrimary / colorBg / logoUrl`.
 */
export default function TabClientContainer() {
  const { t } = useTranslation('tabs');
  const [config, setConfig] = useState({});
  const [slug, setSlug]     = useState(null);
  // Defaults dérivés du resto pour le bouton "Réinitialiser" (nom réel, pas vide).
  const [restoDefaults, setRestoDefaults] = useState({ restoName: '' });
  const toast               = useToast();

  useEffect(() => {
    (async () => {
      try {
        const cfg  = await apiGet('/api/restaurants/config').catch(() => null);
        const dash = await apiGet('/api/dashboard/').catch(() => null);
        const r = cfg || dash?.restaurant || {};
        setSlug(r.slug || null);
        setRestoDefaults({ restoName: r.nom || '' });
        setConfig({
          // Identité
          restoName:   r.nom || '',
          // Messages personnalisés (champs DB pas encore créés — Sprint 1)
          msgWelcome:  r.message_accueil   || '',
          msgThanks:   r.message_satisfait || '',
          msgFeedback: r.message_decu      || '',
          // Couleurs (idem — Sprint 1 pour la persistance)
          colorPrimary: r.couleur_accent || '#E8502A',
          colorBg:      r.couleur_fond   || '#FBF7F0',
          // Logo
          logoUrl: r.logo_url || null,
        });
      } catch (e) {
        console.error('[TabClient] init error:', e);
      }
    })();
  }, []);

  const handleSave = useCallback(async (newConfig) => {
    try {
      // Sprint 1 P0 — payload aligné 1:1 avec la whitelist backend
      // (CONFIG_FIELDS_SCHEMA dans backend/routes/restaurants.js).
      // Validation côté backend : couleurs hex strict #RRGGBB, logo_url
      // doit commencer par https://, messages cap 500 chars.
      // Si l'API renvoie 400 (champ invalide) → on remonte l'erreur à l'utilisateur.
      const payload = {
        message_accueil:   newConfig.msgWelcome   ?? '',
        message_satisfait: newConfig.msgThanks    ?? '',
        message_decu:      newConfig.msgFeedback  ?? '',
        couleur_accent:    newConfig.colorPrimary ?? '',
        couleur_fond:      newConfig.colorBg      ?? '',
      };
      // Logo : envoyé seulement si défini (URL https), sinon on n'envoie pas le champ
      // (= pas de modification côté DB, reste à sa valeur précédente). Pour le clear,
      // envoyer chaîne vide → backend stocke NULL.
      if (newConfig.logoUrl !== undefined) {
        payload.logo_url = newConfig.logoUrl ?? '';
      }
      await apiPut('/api/restaurants/config', payload);
      alert(t('client.alerts.save_success'));
    } catch (e) {
      alert(t('client.alerts.save_failed', { error: e.message }));
    }
  }, [t]);

  const handlePreview = useCallback(() => {
    if (!slug) {
      toast.error(t('client.alerts.slug_missing'));
      return;
    }
    window.open(`https://www.getrevyo.com/r/${slug}`, '_blank', 'noopener,noreferrer');
  }, [t, slug, toast]);

  // Sprint 2 P3 (mai 2026) — feature upload logo retirée le temps que la route
  // POST /api/restaurants/logo soit livrée (Phase 3). Le bouton "Choisir un fichier"
  // est aussi retiré côté <TabClient/> pour ne pas exposer un état dégradé.

  const handleReset = useCallback(() => {
    if (!window.confirm(t('client.alerts.reset_confirm'))) return;
    // Réinitialise aux defaults dérivés du resto (nom réel conservé), pas à vide.
    setConfig({
      restoName:    restoDefaults.restoName,
      msgWelcome:   '',
      msgThanks:    '',
      msgFeedback:  '',
      colorPrimary: '#E8502A',
      colorBg:      '#FBF7F0',
      logoUrl:      null,
    });
  }, [t, restoDefaults]);

  return (
    <TabClient
      config={config}
      onSave={handleSave}
      onPreview={handlePreview}
      onReset={handleReset}
    />
  );
}
